// KLIPY's standard integration loads API responses/media directly from the
// client. No Link token, account id, message body, or identity key is forwarded.
export interface GifItem { id: string; slug: string; title: string; url: string; previewUrl: string; stillUrl?: string; width: number; height: number }
export interface GifFavorite { id?: string; slug: string }
export interface GifCategory { label: string; query: string; previewUrl?: string }
export interface GifPage { items: GifItem[]; hasNext: boolean }
export interface GifClient {
  configured: boolean;
  list: (query: string, page: number, signal: AbortSignal) => Promise<GifPage>;
  categories: (signal: AbortSignal) => Promise<GifCategory[]>;
  items: (slugs: string[], signal: AbortSignal) => Promise<GifPage>;
}

const CDN_HOSTS = new Set(["static.klipy.com", "static1.klipy.com", "static2.klipy.com"]);
export function validGifLink(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password && !/\s/.test(value)
      && /\.(gif|webp)$/i.test(url.pathname) && url.hostname !== "localhost" && !url.hostname.endsWith(".local");
  } catch { return false; }
}
function mediaUrl(value: unknown, still = false): string {
  if (typeof value !== "string") throw new Error("GIF data was incomplete. Try again.");
  let url: URL;
  try { url = new URL(value); } catch { throw new Error("The GIF provider returned an unsupported image."); }
  if (url.protocol !== "https:" || !CDN_HOSTS.has(url.hostname) || url.username || url.password || url.port || !(/\.(gif|webp)$/i.test(url.pathname) || still && /\.(jpg|jpeg|png)$/i.test(url.pathname))) {
    throw new Error("The GIF provider returned an unsupported image.");
  }
  return value; // Preserve the provider's complete URL, including delivery parameters.
}
function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("GIF data was incomplete. Try again.");
  return value as Record<string, unknown>;
}
function slug(value: unknown): string {
  if (typeof value !== "string" || !/^[a-zA-Z0-9_-]{1,180}$/.test(value)) throw new Error("GIF data was incomplete. Try again.");
  return value;
}
function gifId(value: unknown): string {
  if (typeof value === "number" && Number.isSafeInteger(value) && value > 0) return String(value);
  if (typeof value === "string" && /^[0-9]{1,32}$/.test(value)) return value;
  throw new Error("GIF data was incomplete. Try again.");
}
function dimension(value: unknown): number { return typeof value === "number" && value > 0 && value <= 8192 ? value : 220; }
function resultData(value: unknown): Record<string, unknown> {
  const response = record(value);
  if (response.result !== true) throw new Error("GIF search is unavailable. Please try again later.");
  return record(response.data);
}
export function parseGifPage(value: unknown): GifPage {
  const data = resultData(value);
  if (!Array.isArray(data.data) || data.data.length > 60) throw new Error("GIF data was incomplete. Try again.");
  // Do not inject provider HTML, silently filter, or reorder results. This
  // integration requires a GIF-only feed; unsupported payloads fail closed.
  const items = data.data.map((raw): GifItem => {
    const item = record(raw);
    if (item.type !== "gif") throw new Error("This GIF feed isn't supported yet. Please try again later.");
    const files = record(item.file), hd = record(files.hd), sm = record(files.sm ?? files.xs ?? files.hd);
    const full = record(hd.gif ?? hd.webp), small = record(sm.webp ?? sm.gif);
    const still = sm.jpg === undefined ? undefined : mediaUrl(record(sm.jpg).url, true);
    return { id: gifId(item.id), slug: slug(item.slug), title: typeof item.title === "string" ? item.title.slice(0, 160) : "GIF", url: mediaUrl(full.url), previewUrl: mediaUrl(small.url), ...(still ? { stillUrl: still } : {}), width: dimension(small.width), height: dimension(small.height) };
  });
  return { items, hasNext: data.has_next === true };
}
export function parseGifCategories(value: unknown): GifCategory[] {
  const data = resultData(value);
  if (!Array.isArray(data.categories) || data.categories.length > 100) throw new Error("GIF categories were unavailable.");
  return data.categories.map((raw) => {
    const item = record(raw);
    if (typeof item.category !== "string" || typeof item.query !== "string" || item.query.length > 100) throw new Error("GIF categories were incomplete.");
    return { label: item.category.slice(0, 80), query: item.query, previewUrl: mediaUrl(item.preview_url) };
  });
}

export function createGifClient(appKey: string, transport: typeof fetch = fetch): GifClient {
  async function request(endpoint: string, parameters: Record<string, string>, signal: AbortSignal): Promise<unknown> {
    if (!appKey) throw new Error("GIF search hasn't been connected yet. You can still add a GIF link.");
    const url = new URL(`https://api.klipy.com/api/v1/${encodeURIComponent(appKey)}/gifs/${endpoint}`);
    for (const [key, value] of Object.entries(parameters)) url.searchParams.set(key, value);
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) controller.abort();
    const timer = setTimeout(abort, 10000);
    try {
      const response = await transport(url, { signal: controller.signal, credentials: "omit", referrerPolicy: "no-referrer", headers: { Accept: "application/json" } });
      if (response.status === 429) throw new Error("GIF search is busy. Give it a moment, then try again.");
      if (!response.ok) throw new Error("Couldn't load GIFs. Please try again.");
      return await response.json();
    } catch (error) {
      if (signal.aborted) throw error;
      if (controller.signal.aborted) throw new Error("GIF search timed out. Please try again.");
      // Never surface fetch errors that can contain the app key in the URL.
      if (error instanceof TypeError) throw new Error("Couldn't reach GIF search. Check your connection and try again.");
      if (error instanceof SyntaxError) throw new Error("GIF search returned an invalid response. Please try again.");
      throw error;
    } finally { clearTimeout(timer); signal.removeEventListener("abort", abort); }
  }
  return {
    configured: appKey.length > 0,
    async list(query, page, signal) {
      const q = query.trim().slice(0, 100);
      return parseGifPage(await request(q ? "search" : "trending", { page: String(Math.max(1, Math.min(100, page))), per_page: "24", content_filter: "high", format_filter: "gif,webp,jpg", ...(q ? { q } : {}) }, signal));
    },
    async categories(signal) { return parseGifCategories(await request("categories", {}, signal)); },
    async items(slugs, signal) {
      if (slugs.length === 0) return { items: [], hasNext: false };
      return parseGifPage(await request("items", { slugs: slugs.slice(0, 50).map(slug).join(",") }, signal));
    },
  };
}

// This is a provider-issued PUBLIC client app key, never a Link/API admin secret.
export const gifClient = createGifClient(import.meta.env?.VITE_KLIPY_APP_KEY?.trim() ?? "");
export const FAVORITES_LIMIT = 50;
export function matchesGifFavorite(favorite: GifFavorite, item: GifItem): boolean {
  if (favorite.id) return favorite.id === item.id;
  // Legacy slug-only saves may include a search suffix absent from Items.
  // This comparison is local only: always send the full saved slug to KLIPY,
  // and never strip delivery parameters or alter returned media URLs.
  return favorite.slug === item.slug || favorite.slug.startsWith(`${item.slug}--`);
}
function cleanFavorites(value: unknown): GifFavorite[] {
  if (!Array.isArray(value)) return [];
  const result: GifFavorite[] = [], seen = new Set<string>();
  for (const entry of value) {
    try {
      const raw = typeof entry === "string" ? { slug: entry } : record(entry);
      const saved: GifFavorite = { slug: slug(raw.slug), ...(raw.id === undefined ? {} : { id: gifId(raw.id) }) };
      const key = saved.id ? `id:${saved.id}` : `slug:${saved.slug}`;
      if (!seen.has(key)) { result.push(saved); seen.add(key); }
      if (result.length === FAVORITES_LIMIT) break;
    } catch { /* Ignore malformed saved entries without losing valid favorites. */ }
  }
  return result;
}
export function readGifFavorites(storage: Pick<Storage, "getItem">, account: string): GifFavorite[] {
  try {
    const saved: unknown = JSON.parse(storage.getItem(`link.gif-favorites.${account}`) ?? "[]");
    return cleanFavorites(saved);
  } catch { return []; }
}
export function writeGifFavorites(storage: Pick<Storage, "setItem">, account: string, favorites: GifFavorite[]): boolean {
  try { storage.setItem(`link.gif-favorites.${account}`, JSON.stringify(cleanFavorites(favorites))); return true; } catch { return false; }
}
